<template>
  <div class="slider-element" :style="cssProps">
    <div class="labels">
      <label :for="name">{{ label }}</label>
      <span v-if="subText">
        <input
          class="value-display"
          type="number"
          :id="id + '-Input'"
          :value="value"
          :min="min"
          :max="maxValue"
          @input="$emit('input', $event.target.value)"
          @blur="checkValueBoundaries()"
        />
        {{ subText }}
      </span>
    </div>
    <input
      ref="slider"
      type="range"
      :min="min"
      :max="maxValue"
      :id="id"
      :value="value"
      :name="name"
      :step="step"
      @blur="$emit('blur')"
      @input="$emit('input', $event.target.value)"
      @focus="$emit('focus')"
    />

    <div class="steps">
      <div
        class="step-wrapper"
        v-for="(step, index) in _steps"
        :key="index"
        :class="{ selected: value == step }"
        @click="$emit('input', step)"
      >
        <div class="dot-wrapper">
          <div class="dot"></div>
        </div>
        <div class="step">
          <span>
            {{ step }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [Number, String]
    },
    name: {
      required: true,
      type: String
    },
    id: {
      required: true,
      type: String
    },
    min: {
      type: Number,
      default: 0
    },
    step: {
      type: Number,
      default: 100
    },
    steps: {
      type: Number,
      default: 5
    },
    label: {
      type: String
    },
    subText: {
      type: String
    },
    activeColor: {
      type: String,
      default: "var(--orange-web)"
    }
  },
  data() {
    return {
      sliderWidth: 0,
      stepDistance: 0
    };
  },
  methods: {
    checkValueBoundaries() {
      this.$emit("blur");
      if (this.value > this.maxValue) this.$emit("input", this.maxValue);
      else if (this.value < this.min) this.$emit("input", this.min);
    }
  },
  computed: {
    _steps() {
      const steps = [this.min];
      for (let i = 0; i < this.steps; i++) {
        const nextNumber = (i + 1) * this.step + this.min;
        steps.push(nextNumber);
      }
      return steps;
    },
    maxValue() {
      return this.steps * this.step + this.min;
    },
    boundaryChanges(){
      return this.min + this.maxValue
    },
    cssProps(){
      return {"--active-color" : this.activeColor}
    }
  },
  watch: {
    boundaryChanges: {
      handler: function(){
        this.checkValueBoundaries()
      }
    }
  }
};
</script>

<style lang="less">
:root {
  --thumb-size: 12px;
}

.slider-element {
  position: relative;
  width: 100%;
  margin-bottom: 24px; // Push down elements after (because step numbers are placed absolute)
  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 15px;
    height: 2px;
    border-radius: 20px;
    background: #d3d3d3;
    outline: none;
    opacity: 0.7;
    -webkit-transition: 0.2s;
    transition: opacity 0.2s;
    cursor: pointer;
    &:hover,
    &:focus {
      opacity: 1;
    }

    &::-webkit-slider-thumb {
      cursor: grab;
      -webkit-appearance: none;
      appearance: none;
      width: var(--thumb-size);
      height: var(--thumb-size);
      margin-right: 4px;
      border-radius: 50%;
      background: var(--active-color);
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: var(--thumb-size);
      height: var(--thumb-size);
      background: var(--active-color);
      border-radius: 50%;
      cursor: pointer;
    }
  }

  .steps {
    user-select: none;
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    .step-wrapper {
      position: relative;
      background: var(--text-grey-primary);
      font-size: 8px;
      .step {
        position: absolute;
        display: flex;
        width: var(--thumb-size);
        left: 0;
        justify-content: center;
        color: var(--text-grey-primary);
      }
      &:last-child .step {
        justify-content: flex-end;
      }
      &:nth-child(1) .step {
        justify-content: flex-start;
      }
      .dot-wrapper {
        cursor: pointer;
        width: var(--thumb-size);
        height: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        .dot {
          width: 4px;
          border-radius: 20px;
          height: 4px;
          background-color: var(--text-grey-primary);
          position: absolute;
          top: -9px;
        }
      }
      &.selected {
        .dot {
          background-color: var(--active-color);
        }
        .step {
          color: #fff;
        }
      }
    }
  }
  .labels {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: var(--text-grey-primary);

    label {
      font-weight: bold;
    }
  }

  .value-display {
    background: transparent;
    background: var(--charcoal);
    padding: 2px;
    border: none;
    color: inherit;
  }

  input::-webkit-outer-spin-button ,
  input::-webkit-inner-spin-button  {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }

}
</style>
